/**
 * Orik's components in the harness shell's seats.
 *
 * The harness draws the page: its frame (`ui-layout`), its sidebar (`ui-sidebar`) with the conversation list
 * (`ui-workspace`) and the scheduled-task page (`ui-schedule`), and its Settings panel (`ui-settings-general`).
 * Orik fills what they leave to a deployment:
 *
 * - **Brand.** Orik's mark and name in the sidebar's brand row (`sidebar.brand.mark`, `sidebar.brand.name`).
 * - **Account.** The launcher at the foot of the sidebar (`settings.launcher`), in place of the plain Settings
 *   trigger: who is signed in, opening Settings. A page load that asked for a Settings section opens it through a
 *   step of the settings shell's onboarding stage (`settings.onboarding`), the one route the shell gives a registrant.
 * - **Settings pages.** Integrations and Account (`settings.section`), after the harness's own General page, where
 *   the theme's Appearance row lives.
 * - **Dialogs.** The delete confirmation and the browser hand-off, in the frame's overlay layer (`shell.overlay`).
 *
 * Each registration waits for its seat's declaration (`slots.inject`), since the shell's plugins apply in their own
 * order.
 */
import type { ComponentType } from "react";
import { AccountSection, OrikBrandMark, OrikBrandName, OrikLauncher, OrikSettingsLinkStep, DeleteConversationDialog, SETTINGS_LINK_STEP } from "../src/components/harness-shell";
import { IntegrationsSection } from "../src/components/integrations";
import { ACCOUNT_SECTION, INTEGRATIONS_SECTION } from "../src/lib/agent/shell-links";
import type { HarnessContext } from "./orik-harness";

/** Register one occupant once its seat is declared, for the lifetime of the kernel. */
function occupy(ctx: HarnessContext, seat: string, options: Record<string, unknown>, component: ComponentType<never>): void {
  ctx.slots.inject(seat, () => ctx.slots.register({ name: seat, ...options }, component));
}

/** Register every Orik occupant of the shell. */
export function installOrikShell(ctx: HarnessContext): void {
  occupy(ctx, "sidebar.brand.mark", {}, OrikBrandMark as ComponentType<never>);
  occupy(ctx, "sidebar.brand.name", {}, OrikBrandName as ComponentType<never>);
  occupy(ctx, "settings.launcher", {}, OrikLauncher as ComponentType<never>);
  // Before any onboarding step of the harness's own, so a page load's link is never queued behind one.
  occupy(ctx, "settings.onboarding", { id: SETTINGS_LINK_STEP, order: -1000 }, OrikSettingsLinkStep as ComponentType<never>);
  occupy(ctx, "settings.section", { id: INTEGRATIONS_SECTION, order: 100, label: "Integrations" }, IntegrationsSection as ComponentType<never>);
  occupy(ctx, "settings.section", { id: ACCOUNT_SECTION, order: 200, label: "Account" }, AccountSection as ComponentType<never>);
  occupy(ctx, "shell.overlay", { id: "orik-delete-conversation" }, DeleteConversationDialog as ComponentType<never>);
}
